// MissedCallsTable component
import { Flex, FlexProps, Icon, Text } from '@chakra-ui/react'
import { ArrowDown } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoadingSpinner } from '~/components/shared/LoadingSpinner'
import { ScrollingList } from '~/components/shared/ScrollingList'
import {
  useMissedCallsTableHeaderCellStyles,
  useMissedCallsTableStyles,
} from '~/pages/MissedCalls/componentStyles/MissedCallsTable.style'
import {
  MissedCallsTableColumn,
  MissedCallsTableColumnSorting,
} from '~/pages/MissedCalls/data/MissedCallsTableData'
import { SortDirection } from '~/types/Shared'
import { Size } from '~/types/ThemeTypes'

import { MissedCallListItem } from './MissedCallListItem'
import { MissedCall } from './types/MissedCall'

interface TableHeaderCellProps extends FlexProps {
  columnType: MissedCallsTableColumn
  sortDirection?: SortDirection
  sortable?: boolean
  onSortClick?: (
    columnType: MissedCallsTableColumn,
    currentSortDirection?: SortDirection,
  ) => void
  tableStyles: ReturnType<typeof useMissedCallsTableStyles>
}

const getMissedCallsTableHeaderCellData = (t: (key: string) => string) => ({
  [MissedCallsTableColumn.DateTime]: {
    title: t('missedCallsTable.columns.dateTime'),
    styleType: MissedCallsTableColumn.DateTime,
  },
  [MissedCallsTableColumn.From]: {
    title: t('missedCallsTable.columns.from'),
    styleType: MissedCallsTableColumn.From,
  },
  [MissedCallsTableColumn.To]: {
    title: t('missedCallsTable.columns.to'),
    styleType: MissedCallsTableColumn.To,
  },
  [MissedCallsTableColumn.Type]: {
    title: t('missedCallsTable.columns.type'),
    styleType: MissedCallsTableColumn.Type,
  },
  [MissedCallsTableColumn.FollowUpTime]: {
    title: t('missedCallsTable.columns.followUpTime'),
    styleType: MissedCallsTableColumn.FollowUpTime,
  },
  [MissedCallsTableColumn.FollowUpStatus]: {
    title: t('missedCallsTable.columns.followUpStatus'),
    styleType: MissedCallsTableColumn.FollowUpStatus,
  },
  [MissedCallsTableColumn.AssignedTo]: {
    title: t('missedCallsTable.columns.assignedTo'),
    styleType: MissedCallsTableColumn.AssignedTo,
  },
  [MissedCallsTableColumn.Action]: {
    title: t('missedCallsTable.columns.action'),
    styleType: MissedCallsTableColumn.Action,
  },
})

const TableHeaderCell = ({
  columnType,
  sortDirection: externalSortDirection,
  sortable,
  onSortClick,
  tableStyles,
}: TableHeaderCellProps) => {
  const { t } = useTranslation()
  const [hovered, setIsHovered] = useState(false)
  const { title, styleType } = getMissedCallsTableHeaderCellData(t)[columnType]
  const columnStyle = tableStyles[styleType]
  const styles = useMissedCallsTableHeaderCellStyles()
  const headerStyle = styles[styleType]

  const resolvedSortDirection = externalSortDirection
  const iconRotation =
    resolvedSortDirection === SortDirection.Ascending ? 180 : 0

  const isInteractable = resolvedSortDirection || (hovered && sortable)
  return (
    <Flex
      css={{ ...columnStyle, ...headerStyle }}
      cursor={isInteractable ? 'pointer' : 'default'}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={
        isInteractable
          ? () => onSortClick?.(columnType, resolvedSortDirection)
          : undefined
      }
    >
      <Text css={styles.headerText}>{title}</Text>
      {isInteractable && (
        <Icon
          as={ArrowDown}
          css={{
            ...styles.sortIcon,
            transform: `rotate(${iconRotation}deg)`,
          }}
        />
      )}
    </Flex>
  )
}

interface MissedCallsTableProps extends FlexProps {
  missedCalls: MissedCall[]
  selectedMissedCallId?: number
  isLoading?: boolean
  tableSize: Size
  columnSorting?: MissedCallsTableColumnSorting[]
  onSortableColumnClicked?: (
    columnType: MissedCallsTableColumn,
    currentSortDirection?: SortDirection,
  ) => void
  onEndReached?: () => void
  onFollowUpStatusChanged?: (missedCallId: number, newStatus: string) => void
  assigneeUsers?: { id: number; full_name?: string | null }[]
  isAssigneeUsersLoading?: boolean
  onAssigneeChanged?: (missedCallId: number, userId: number | null) => void
}

export const MissedCallsTable = ({
  missedCalls,
  isLoading,
  tableSize,
  columnSorting,
  onSortableColumnClicked,
  onEndReached,
  onFollowUpStatusChanged,
  assigneeUsers,
  isAssigneeUsersLoading,
  onAssigneeChanged,
  ...rest
}: MissedCallsTableProps) => {
  const tableStyles = useMissedCallsTableStyles({ size: tableSize })
  const styles = useMissedCallsTableHeaderCellStyles({ size: tableSize })
  const columnSortingMap = useMemo(() => {
    return new Map(
      columnSorting?.map(({ column, direction }) => [column, direction]),
    )
  }, [columnSorting])

  const showTableLoader = isLoading && missedCalls.length === 0

  const tableContent = useMemo(() => {
    if (showTableLoader) {
      return (
        <Flex justify="center" align="center" boxSize="full">
          <LoadingSpinner size={Size.lg} />
        </Flex>
      )
    }

    return (
      <Flex css={styles.listContainer}>
        <ScrollingList
          display="flex"
          height="100%"
          width="full"
          bg="bg.subtle"
          itemData={missedCalls}
          showLoaderFooter={isLoading}
          onEndReached={onEndReached}
          scrollbarOptions={{ showOnHoverOnly: true }}
          onBuildItemContent={(index, missedCall, isSelected) => (
            <MissedCallListItem
              key={index}
              missedCall={missedCall}
              isSelected={isSelected}
              tableSize={tableSize}
              isFirst={index === 0 ? true : undefined}
              onFollowUpStatusChanged={onFollowUpStatusChanged}
              users={assigneeUsers}
              isUsersLoading={isAssigneeUsersLoading}
              onAssigneeChanged={onAssigneeChanged}
            />
          )}
        />
      </Flex>
    )
  }, [
    missedCalls,
    showTableLoader,
    tableSize,
    onEndReached,
    onFollowUpStatusChanged,
    assigneeUsers,
    isAssigneeUsersLoading,
    onAssigneeChanged,
  ])

  return (
    <Flex css={styles.container} {...rest}>
      <Flex css={styles.tableHeader}>
        <Flex css={styles.headerColumnWrapper}>
          <TableHeaderCell
            columnType={MissedCallsTableColumn.DateTime}
            sortDirection={columnSortingMap.get(
              MissedCallsTableColumn.DateTime,
            )}
            sortable={columnSortingMap.has(MissedCallsTableColumn.DateTime)}
            onSortClick={onSortableColumnClicked}
            tableStyles={tableStyles}
          />
          <TableHeaderCell
            columnType={MissedCallsTableColumn.From}
            sortDirection={columnSortingMap.get(MissedCallsTableColumn.From)}
            sortable={columnSortingMap.has(MissedCallsTableColumn.From)}
            onSortClick={onSortableColumnClicked}
            tableStyles={tableStyles}
          />
          <TableHeaderCell
            columnType={MissedCallsTableColumn.To}
            sortDirection={columnSortingMap.get(MissedCallsTableColumn.To)}
            sortable={columnSortingMap.has(MissedCallsTableColumn.To)}
            onSortClick={onSortableColumnClicked}
            tableStyles={tableStyles}
          />
          <TableHeaderCell
            columnType={MissedCallsTableColumn.Type}
            sortDirection={columnSortingMap.get(MissedCallsTableColumn.Type)}
            sortable={columnSortingMap.has(MissedCallsTableColumn.Type)}
            onSortClick={onSortableColumnClicked}
            tableStyles={tableStyles}
          />
          <TableHeaderCell
            columnType={MissedCallsTableColumn.FollowUpTime}
            sortDirection={columnSortingMap.get(
              MissedCallsTableColumn.FollowUpTime,
            )}
            sortable={columnSortingMap.has(MissedCallsTableColumn.FollowUpTime)}
            onSortClick={onSortableColumnClicked}
            tableStyles={tableStyles}
          />
          <TableHeaderCell
            columnType={MissedCallsTableColumn.FollowUpStatus}
            sortDirection={columnSortingMap.get(
              MissedCallsTableColumn.FollowUpStatus,
            )}
            sortable={columnSortingMap.has(
              MissedCallsTableColumn.FollowUpStatus,
            )}
            onSortClick={onSortableColumnClicked}
            tableStyles={tableStyles}
          />
          <TableHeaderCell
            columnType={MissedCallsTableColumn.AssignedTo}
            sortDirection={columnSortingMap.get(
              MissedCallsTableColumn.AssignedTo,
            )}
            sortable={columnSortingMap.has(MissedCallsTableColumn.AssignedTo)}
            onSortClick={onSortableColumnClicked}
            tableStyles={tableStyles}
          />
          <TableHeaderCell
            columnType={MissedCallsTableColumn.Action}
            sortDirection={columnSortingMap.get(MissedCallsTableColumn.Action)}
            sortable={columnSortingMap.has(MissedCallsTableColumn.Action)}
            onSortClick={onSortableColumnClicked}
            tableStyles={tableStyles}
          />
        </Flex>
      </Flex>
      {tableContent}
    </Flex>
  )
}
